import type React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { DashboardPreset } from "../../types/dashboard";
import type { OperationsTeam } from "../../types/operationsTeam";

type TranslationKeys = keyof typeof translations.en;

const PRESETS: DashboardPreset[] = [
  "today",
  "yesterday",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "custom",
];

const PRESET_LABEL_KEYS: Record<DashboardPreset, TranslationKeys> = {
  today: "dashboard.periodToday",
  yesterday: "dashboard.periodYesterday",
  this_week: "dashboard.periodThisWeek",
  last_week: "dashboard.periodLastWeek",
  this_month: "dashboard.periodThisMonth",
  last_month: "dashboard.periodLastMonth",
  custom: "dashboard.periodCustom",
};

interface DashboardPeriodControlsProps {
  preset: DashboardPreset;
  teamOperationsId: string;
  teams: OperationsTeam[];
  refreshing?: boolean;
  onPresetChange: (preset: DashboardPreset) => void;
  onTeamChange: (teamOperationsId: string) => void;
}

const DashboardPeriodControls: React.FC<DashboardPeriodControlsProps> = ({
  preset,
  teamOperationsId,
  teams,
  refreshing,
  onPresetChange,
  onTeamChange,
}) => {
  const { t } = useTranslation();

  const handlePresetChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: DashboardPreset | null,
  ) => {
    if (value) {
      onPresetChange(value);
    }
  };

  const handleTeamChange = (event: SelectChangeEvent<string>) => {
    onTeamChange(event.target.value);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
        justifyContent: "flex-end",
      }}
      data-testid={TestId.DASHBOARD.PERIOD}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={preset}
        onChange={handlePresetChange}
        sx={{ flexWrap: "wrap" }}
      >
        {PRESETS.map((value) => (
          <ToggleButton
            key={value}
            value={value}
            data-testid={TestId.DASHBOARD.PERIOD_OPTION(value)}
          >
            {t(PRESET_LABEL_KEYS[value])}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="dashboard-team-filter-label">{t("Teams" as TranslationKeys)}</InputLabel>
        <Select
          labelId="dashboard-team-filter-label"
          label={t("Teams" as TranslationKeys)}
          value={teamOperationsId}
          onChange={handleTeamChange}
          data-testid={TestId.DASHBOARD.TEAM_FILTER}
        >
          <MenuItem value="">{t("dashboard.allTeams" as TranslationKeys)}</MenuItem>
          {teams.map((team) => (
            <MenuItem key={team._id} value={team._id}>
              {team.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {refreshing && (
        <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>
          {t("dashboard.refreshing" as TranslationKeys)}
        </Box>
      )}
    </Box>
  );
};

export default DashboardPeriodControls;
